import type { DataTableColumnDef } from '@neo/blocks/cx-data-table'
import type { DataTableFilterField } from '@neo/blocks/data-table/types'
import { Avatar, AvatarFallback } from '@neo/primitives/avatar'
import { Button } from '@neo/primitives/button'
import { StatusIndicator } from '@neo/primitives/status-indicator'
import { Tooltip } from '@neo/primitives/tooltip'
import { Typography } from '@neo/primitives/typography'
import { getInitials, pluralize } from '@shared/utils'
import { ChevronDown, ChevronUp } from 'lucide-react'
import type { RUserSession } from 'v2/types/api/auth'
import type { RExternalOrgApp } from 'v2/types/api/settings'
import dayjs from 'v2/utils/dayjs'
import { getAccountIdentifier } from '../utils/account-identifier'
import { IntegrationState } from '../utils/constants'
import {
  effectiveIntegrationState,
  getIntegrationStatusOptions,
  RECONNECT_REQUIRED_STATE,
  requiresAciReconnect,
} from '../utils/integration-availability'
import { ExternalAppActions, type ExternalAppRow } from './external-app-actions'
import { useReconnectApp } from './hooks/use-reconnect-app'

export { getExternalAppsContextMenu } from './external-apps-context-menu'

export const getColumns = (
  currentUser?: RUserSession,
  aciEnabled = false,
): DataTableColumnDef<ExternalAppRow>[] => [
  {
    id: 'expander',
    header: 'Tool',
    Cell: ({ row }) =>
      row.getCanExpand() ? (
        <div data-testid="expander" className="text-tertiary">
          {row.getIsExpanded() ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </div>
      ) : null,
    size: 32,
    enableSorting: false,
    enableHiding: false,
  },
  {
    accessorKey: 'name',
    header: '',
    filterFn: (row, _, filterValue) => {
      const name = row.original.name?.toLowerCase() || ''
      return name.includes(filterValue?.toLowerCase())
    },
    Cell: ({ row }) => {
      const { isGroup, subRows } = row.original
      const expiredConnectionsCount =
        subRows?.filter((subRow) => subRow.state === IntegrationState.EXPIRED)?.length || 0
      const connectionsCount = subRows?.length || 0

      if (isGroup) {
        return (
          <div className="flex items-center gap-2">
            <div className="p-2 bg-bg3 rounded-md">
              <img
                src={row.original.logo_url}
                alt={row.original.name}
                className="h-6 w-6 rounded-full"
              />
            </div>
            <div className="flex flex-col items-start">
              <Typography variant="S/500">{row.original.name}</Typography>
              {Array.isArray(subRows) && subRows.length > 0 && (
                <div className="flex items-center gap-2">
                  {connectionsCount > 0 && expiredConnectionsCount !== connectionsCount && (
                    <Typography variant="XS/400" className="text-secondary">
                      {pluralize(connectionsCount, 'Connection')}
                    </Typography>
                  )}
                  {expiredConnectionsCount > 0 && (
                    <Typography
                      variant="XS/400"
                      className="text-destructive flex items-center gap-1"
                    >
                      {expiredConnectionsCount === connectionsCount ? (
                        pluralize(expiredConnectionsCount, 'Expired Connection')
                      ) : (
                        <>
                          <span className="text-tertiary">•</span>
                          {`${expiredConnectionsCount} Expired`}
                        </>
                      )}
                    </Typography>
                  )}
                </div>
              )}
            </div>
          </div>
        )
      }

      return (
        <div className="flex items-center gap-2 ml-4">
          <div>
            <Typography variant="S/400"> {getAccountIdentifier(row.original)}</Typography>
            {row.original.provider === 'slack' &&
              typeof row.original.account_identifier?.team === 'string' && (
                <Typography variant="XS/400" className="text-secondary">
                  {row.original.account_identifier.team}
                </Typography>
              )}
          </div>
        </div>
      )
    },
  },
  {
    accessorKey: 'state',
    header: 'Status',
    Cell: ({ row }) => {
      const { state, isGroup } = row.original
      const { reconnectApp } = useReconnectApp()
      if (isGroup) return null
      const displayState = effectiveIntegrationState(row.original, aciEnabled)
      const getTone = () => {
        switch (displayState) {
          case IntegrationState.ACTIVE:
            return 'success'
          case IntegrationState.EXPIRED:
            return 'danger'
          case IntegrationState.IN_PROGRESS:
            return 'progress'
          case RECONNECT_REQUIRED_STATE:
            return 'warning'
          case IntegrationState.DISABLED:
            return 'disabled'
          default:
            return 'neutral'
        }
      }

      return (
        <div className="flex items-center gap-2">
          <StatusIndicator tone={getTone()}>
            {displayState === RECONNECT_REQUIRED_STATE
              ? 'Reconnect required'
              : state.charAt(0).toUpperCase() + state.slice(1).toLowerCase()}
          </StatusIndicator>
          {state === IntegrationState.EXPIRED &&
            !requiresAciReconnect(row.original, aciEnabled) && (
              <Button
                variant="ghost"
                className="underline hover:bg-transparent"
                data-testid="button-reconnect"
                onClick={(e) => {
                  e.stopPropagation()
                  reconnectApp(row.original)
                }}
              >
                Reconnect
              </Button>
            )}
        </div>
      )
    },
    filterFn: (row, _, filterValue) => {
      if (!Array.isArray(filterValue)) return false
      const state = row.original.state?.toLowerCase() || ''
      const filters = filterValue?.map((f: string) => f.toLowerCase()) || []
      return filters.some((filter: string) => state.includes(filter))
    },
  },
  {
    accessorKey: 'integrated_by',
    header: 'Owner',
    Cell: ({ row }) => {
      const { isGroup } = row.original
      if (isGroup) return null

      return (
        <Tooltip content={row.original.integrated_by?.name}>
          <Avatar className="w-6 h-6">
            <AvatarFallback className="bg-bg3 text-foreground text-xs-500">
              {getInitials(row.original.integrated_by?.name)}
            </AvatarFallback>
          </Avatar>
        </Tooltip>
      )
    },
  },
  {
    accessorKey: 'created_on',
    header: 'Created On',
    Cell: ({ row }) => {
      const { isGroup } = row.original
      if (isGroup) return null

      return (
        <Typography variant="XS/400" className="font-jetbrains tabular-nums">
          {dayjs(row.original.created_on).format('MMM D, YYYY hh:mm A')}
        </Typography>
      )
    },
  },
  {
    id: 'actions',
    header: '',
    Cell: ({ row }) => (
      <ExternalAppActions app={row.original} currentUser={currentUser} aciEnabled={aciEnabled} />
    ),
    size: 120,
    enableSorting: false,
    enableHiding: false,
  },
]

export const getFilterFields = (aciEnabled = false): DataTableFilterField<RExternalOrgApp>[] => [
  {
    label: 'Name',
    value: 'name',
    paramKey: 'connection_details',
    placeholder: 'Search',
  },
  {
    label: 'Status',
    value: 'state',
    paramKey: 'state',
    options: getIntegrationStatusOptions(aciEnabled),
  },
]

// Transform flat app data into grouped structure
export const buildGroupedTableRows = (apps: RExternalOrgApp[]): ExternalAppRow[] => {
  // Group apps by provider
  const grouped = apps.reduce(
    (acc, app) => {
      const provider = app.provider || app.name
      if (!acc[provider]) {
        acc[provider] = []
      }
      acc[provider].push(app)
      return acc
    },
    {} as Record<string, RExternalOrgApp[]>,
  )

  // Transform into grouped rows
  return Object.entries(grouped).map(([provider, providerApps]) => {
    const firstApp = providerApps[0]

    const groupRow: ExternalAppRow = {
      ...firstApp, // Include all properties from RExternalOrgApp
      id: `group-${provider}`,
      name: firstApp.name,
      state: '',
      created_on: '',
      provider,
      isGroup: true,
      subRows: providerApps.map((app) => ({
        ...app, // Include all original app properties first
        provider: app.provider || app.name,
        isGroup: false,
      })),
    }

    return groupRow
  })
}
